import moment from 'moment';
import store from '@/store';
import setupI18n from '../setupI18n';
import { encodePassengers } from './Reserbus';
import { isFreeFareCategory } from './freeFareDocumentRules';

/**
 * Retrieves search parameters from the global state to build a search URL.
 * Returns early with `{ isExchange: true }` if `isExchange` is active, skipping additional parameters.
 *
 * @returns {Object} - Search parameters including:
 *   - `isExchange` {boolean}: Indicates if the operation is an exchange.
 *   - `needsCityId` {boolean}: Specifies if the city ID is required in the search URL.
 *   - `origin` {string}: Origin ID.
 *   - `originCityId` {string}: City ID of the origin.
 *   - `destination` {string}: Destination ID.
 *   - `destinationCityId` {string}: City ID of the destination.
 *   - `departureIsOpenTicket` {boolean}: Indicates if the departure ticket is open.
 *   - `returnIsOpenTicket` {boolean}: Indicates if the return ticket is open.
 *   - `searchDepartureDate` {string}: Search query's departure date.
 *   - `searchReturnsDate` {string}: Search query's return date.
 *   - `departureDate` {string}: Purchase's departure date.
 *   - `returnDate` {string}: Purchase's return date.
 *   - `roundTrip` {boolean}: Indicates if it is a round trip.
 *   - `encodedPassengersSelection` {string}: Encoded string of passenger selection.
 */
export function getSearchParams() {
  const state = store.getState();

  const isExchange = state.purchase.get('isExchange');
  if (isExchange) return { isExchange };

  const passengersSelection = state.purchase.get('passengerSelection')?.toJS();
  const origin = state.purchase.getIn(['departs', 'origin', 'id']);
  const originCityId = state.purchase.getIn(['departs', 'origin', 'cityId']);
  const destination = state.purchase.getIn(['departs', 'destination', 'id']);
  const destinationCityId = state.purchase.getIn(['departs', 'destination', 'cityId']);
  const departureIsOpenTicket = state.purchase.getIn(['departs', 'fragments', 0, 'openTicket']);
  const returnIsOpenTicket = state.purchase.getIn(['returns', 'fragments', 0, 'openTicket']);
  const searchDepartureDate = state.search.get('departureDate');
  const searchReturnsDate = state.search.get('returnDate');
  const departureDate = state.purchase.getIn(['departs', 'departure']);
  const returnDate = state.purchase.getIn(['returns', 'departure']);
  const roundTrip = state.purchase.get('roundTrip');
  const encodedPassengersSelection =
    (passengersSelection && encodePassengers(passengersSelection)) || 'A1';
  const { features } = state.whitelabelConfig;

  return {
    isExchange,
    needsCityId: features.ENABLE_CITY_ID_IN_SEARCH,
    origin,
    originCityId,
    destination,
    destinationCityId,
    departureIsOpenTicket,
    returnIsOpenTicket,
    searchDepartureDate,
    searchReturnsDate,
    departureDate,
    returnDate,
    roundTrip,
    encodedPassengersSelection,
  };
}

/**
 * Generates a URL for redirecting to the search page based on provided parameters.
 * If the `isExchange` flag is true, it directly returns the `/exchange` URL.
 * Formats the departure and return dates based on ticket type (open or specific date).
 * Chooses between city ID or location ID depending on the `needsCityId` feature flag.
 * Constructs the URL using the origin, destination, and formatted date parameters.
 *
 * @param {Object} params - The parameters used to build the URL.
 * @param {boolean} params.needsCityId - Whether to use city IDs in the URL.
 * @param {string} params.origin - Origin ID for the search.
 * @param {string} params.originCityId - Origin city ID for the search.
 * @param {string} params.destination - Destination ID for the search.
 * @param {string} params.destinationCityId - Destination city ID for the search.
 * @param {boolean} params.departureIsOpenTicket - If true, uses an open ticket for the departure date.
 * @param {boolean} params.returnIsOpenTicket - If true, uses an open ticket for the return date.
 * @param {string} params.searchDepartureDate - The searched departure date in 'DD-MM-YYYY' format.
 * @param {string} params.searchReturnsDate - The searched return date in 'DD-MM-YYYY' format.
 * @param {string} params.departureDate - The actual departure date.
 * @param {string} params.returnDate - The actual return date.
 * @param {boolean} params.roundTrip - If true, a return trip is included.
 * @param {boolean} params.isExchange - If true, redirects to the exchange page.
 * @param {string} params.encodedPassengersSelection - Encoded passengers information for the URL.
 * @returns {string} - The generated URL for the search redirect.
 */
export const generateSearchRedirectUrl = (params = {}) => {
  const { isExchange } = params;
  if (isExchange) return '/exchange';

  const {
    needsCityId,
    origin,
    originCityId,
    destination,
    destinationCityId,
    departureIsOpenTicket,
    returnIsOpenTicket,
    searchDepartureDate,
    searchReturnsDate,
    departureDate,
    returnDate,
    roundTrip,
    encodedPassengersSelection = 'A1',
  } = params;

  const formattedDeparture = departureIsOpenTicket
    ? moment(searchDepartureDate, 'DD-MM-YYYY').format('DD-MMM-YY')
    : moment(departureDate).format('DD-MMM-YY');

  let url = `/search/${needsCityId ? originCityId : origin}/${
    needsCityId ? destinationCityId : destination
  }/${formattedDeparture}/`;

  if (roundTrip) {
    const formattedReturn = returnIsOpenTicket
      ? moment(searchReturnsDate, 'DD-MM-YYYY').format('DD-MMM-YY')
      : moment(returnDate).format('DD-MMM-YY');

    url += `${formattedReturn}/`;
  }

  return `${url}p/${encodedPassengersSelection}/departures`;
};

/**
 * Search-redirect URL overrides for the back-to-search "undo" when the URL
 * carries widget-category passenger types that the default purchase-side
 * flow would lose (free-fare categories like `older`, `disability`,
 * `disadvantaged_youth`, plus the form-only `child_in_arms`).
 *
 * Returns `null` when not applicable — callers must keep their existing
 * default redirect URL in that case.
 *
 * Activates when ALL of:
 *   1. `SEARCH_PASSENGERS=true` — legacy `A1`-style format would emit
 *      unparseable `O1` for `older`, etc.
 *   2. The selection carries at least one `FREE_FARE_CATEGORIES` entry,
 *      OR a non-zero `child_in_arms` count. URL-derived
 *      `state.search.passengers` is the primary source. The
 *      backend-persisted `purchase.passengerSelection` is the secondary
 *      source — it captures the same widget-category mix on purchase
 *      creation and survives page reloads on `/purchase/<token>/...` URLs
 *      that don't carry the search slug.
 *
 * Date format follows `env.searchWidget.dateFormat` (fallback
 * `DD-MMM-YY`). Dates also fall back to the purchase's `departs`/`returns`
 * `departure` ISO time when the search slice was reset (page reload on
 * `/purchase/...` URLs that don't carry the search params). Locale is
 * restored from the live i18n instance because the purchase pages drop
 * `?lang` from the URL between steps.
 *
 * @returns {{
 *   encodedPassengers: string,
 *   querySuffix: string,
 *   departureDate: string|null,
 *   returnDate: string|null,
 * }|null}
 */
export const getWidgetCategorySearchRedirectOverrides = () => {
  try {
    const state = store.getState();

    if (!state.whitelabelConfig?.features?.SEARCH_PASSENGERS) return null;

    let passengerCounts = null;

    const searchPassengers = state.search?.get?.('passengers')?.toJS?.();
    if (searchPassengers && Object.keys(searchPassengers).length > 0) {
      const hasFreeFare = Object.keys(searchPassengers).some(
        (category) => Number(searchPassengers[category]) > 0 && isFreeFareCategory(category),
      );
      const hasChildInArms = Number(searchPassengers.child_in_arms || 0) > 0;
      if (hasFreeFare || hasChildInArms) passengerCounts = searchPassengers;
    }

    let purchaseJs = null;
    if (!passengerCounts) {
      purchaseJs = state.purchase?.toJS?.();
      const selection = purchaseJs?.passengerSelection;
      if (selection && typeof selection === 'object') {
        const hasFreeFare = Object.keys(selection).some(
          (category) => Number(selection[category]) > 0 && isFreeFareCategory(category),
        );
        const hasChildInArms = Number(selection.child_in_arms || 0) > 0;
        if (hasFreeFare || hasChildInArms) passengerCounts = selection;
      }
    }

    if (!passengerCounts) return null;

    const encodedPassengers = encodePassengers(passengerCounts);
    if (!encodedPassengers) return null;

    const tenantDateFormat = (
      state.whitelabelConfig?.env?.searchWidget?.dateFormat || 'DD-MMM-YY'
    ).toUpperCase();
    /**
     * Reformat a stored `DD-MM-YYYY` date string into the tenant's URL date format.
     * @param {string|null|undefined} stored - Date as held in `state.search`.
     * @returns {string|null} Tenant-formatted date or null if unparseable.
     */
    const formatStoredDate = (stored) => {
      if (!stored) return null;
      const parsed = moment(stored, 'DD-MM-YYYY');
      return parsed.isValid() ? parsed.format(tenantDateFormat) : null;
    };

    let storedDepartureDate = state.search?.get?.('departureDate');
    let storedReturnDate = state.search?.get?.('returnDate');

    if (!storedDepartureDate || !storedReturnDate) {
      if (!purchaseJs) purchaseJs = state.purchase?.toJS?.();
      if (!storedDepartureDate && purchaseJs?.departs?.departure) {
        storedDepartureDate = moment(purchaseJs.departs.departure).format('DD-MM-YYYY');
      }
      if (!storedReturnDate && purchaseJs?.returns?.departure) {
        storedReturnDate = moment(purchaseJs.returns.departure).format('DD-MM-YYYY');
      }
    }

    const departureDate = formatStoredDate(storedDepartureDate);
    if (!departureDate) return null;
    const returnDate = formatStoredDate(storedReturnDate);

    let querySuffix = '';
    try {
      const lang = setupI18n()?.language;
      if (lang) {
        const params = new URLSearchParams();
        params.set('lang', lang);
        querySuffix = `?${params.toString()}`;
      }
    } catch (langErr) {
      // If i18n is unavailable, fall through with no querySuffix
    }

    return { encodedPassengers, querySuffix, departureDate, returnDate };
  } catch (err) {
    console.warn('Free-fare redirect override skipped; falling back to default', err);
    return null;
  }
};
